import React, { Children, cloneElement, isValidElement } from 'react';
import PropTypes from 'prop-types';
// components
import { Table } from 'semantic-ui-react';
import DefaultButton from 'app/components/commons/button/defaultButton/DefaultButton';
// utils
import _ from 'lodash';
import { capitalize } from 'app/utils';
// consts
import { COLORS } from 'app/config/colors';
// assets
import PlusIcon from 'assets/icon/plus-icon/PlusIcon';
// stylesheet
import styles from './List.module.scss';

function List(props) {
  const onClickIncreaseListLimit = () => {
    props.onClickIncreaseList();
  };

  const buildCells = obj => {
    const getTagBody = (property, obj, isLimitSize) => {
      if (!property) return null;

      const value = _.get(obj, property);
      const limitSizeColumn = isLimitSize && limitSize(capitalize(value));

      if (limitSizeColumn) {
        return limitSizeColumn;
      } else if (value) {
        return value;
      } else {
        return '--';
      }
    };

    const cells = columns.map(column => {
      const { id, width, textAlign, property, headerTag, bodyNode, isLimitSize } = column;
      return {
        id,
        width,
        textAlign,
        headerTag,
        tagBody: getTagBody(property, obj, isLimitSize),
        bodyNode: bodyNode || null
      };
    });

    return cells;
  };

  const { rows, columns, children, loading } = props;

  const limitSize = info => {
    if (info.length > 50) {
      return info.substr(0, 50) + '...';
    }
    return info;
  };

  return (
    <div className={styles.list}>
      <Table id='list-menu' className={styles.table}>
        <Table.Header>
          <Table.Row>
            {columns.map(column => {
              const { id, width, textAlign, headerTag } = column;
              return (
                <Table.HeaderCell key={id} width={width} style={{ textAlign }}>
                  {headerTag}
                </Table.HeaderCell>
              );
            })}
            {children && <Table.HeaderCell width={2} />}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {rows.map(row => {
            const { id } = row;
            const cells = buildCells(row);

            return (
              <Table.Row key={id}>
                {cells.map(cell => {
                  let { id, width, textAlign, tagBody, bodyNode } = cell;

                  let node = tagBody;

                  if (typeof bodyNode === 'function') {
                    bodyNode = bodyNode(row);
                  }

                  if (isValidElement(bodyNode)) {
                    const props = { row: row, entity: row };

                    if (bodyNode.props.onClick) {
                      props.onClick = e => bodyNode.props.onClick(e, row);
                    }

                    // node = cloneElement(bodyNode, { ...props }, tagBody);
                    node = Children.map(bodyNode, childLevelOne => {
                      if (isValidElement(childLevelOne)) {
                        if (childLevelOne.props.children) {
                          return Children.map(childLevelOne.props.children, child => {
                            if (isValidElement(child)) {
                              let children = child.props.children ? [...child.props.children] : [];
                              // let children = [...child.props.children];
                              return cloneElement(child, { ...props }, [...children]);
                            } else {
                              return cloneElement(childLevelOne, { ...props }, child);
                            }
                          });
                        } else {
                          return cloneElement(childLevelOne, { ...props }, tagBody);
                        }
                      }
                    });
                  }
                  return (
                    <Table.Cell key={id} width={width} style={{ textAlign }}>
                      {node}
                    </Table.Cell>
                  );
                })}
                {children && (
                  <Table.Cell textAlign='center' className={styles.popUpAlign} width={2}>
                    {Children.map(children, child => {
                      if (isValidElement(child)) {
                        return cloneElement(child, { row: row, entity: row });
                      }
                    })}
                  </Table.Cell>
                )}
              </Table.Row>
            );
          })}
          {
            <Table.Row>
              <Table.Cell colSpan='12'>
                <span className={styles.increaseButton}>
                  <DefaultButton
                    tag=''
                    loading={loading}
                    size='medium'
                    backgroundColor={COLORS.WHITE_VARIANT}
                    backgroundColorHover={COLORS.ACCENTUATED_VARIANT}
                    icon={<PlusIcon fill={COLORS.ACCENTUATED_VARIANT} />}
                    iconHover={<PlusIcon fill={COLORS.WHITE_VARIANT} />}
                    onClick={onClickIncreaseListLimit}
                  />
                </span>
              </Table.Cell>
            </Table.Row>
          }
        </Table.Body>
      </Table>
    </div>
  );
}

List.defaultProps = {
  columns: [],
  loading: false,
  onClickIncreaseList: () => {},
  rows: {}
};

List.propTypes = {
  children: PropTypes.node,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      width: PropTypes.number.isRequired,
      textAlign: PropTypes.oneOf(['left', 'center', 'right']).isRequired,
      headerTag: PropTypes.string.isRequired,
      bodyNode: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
      property: PropTypes.string
    })
  ),
  loading: PropTypes.bool,
  onClickIncreaseList: PropTypes.func,
  rows: PropTypes.array
};

export default List;
